import Colors from './color';
import { IntentCard, Intents } from '../types/intent';

export interface IntentButtonColors {
  backgroundColor: string;
  // Always a concrete color (never undefined) so it can be dropped straight into
  // MUI's `sx`, whose index signature rejects undefined values.
  color: string;
}

// HITL 3.0 groups intents into three sections, each rendered as one solid color.
// Only used when isHitl3 — the legacy layout keeps each card's own colors.
//   Conversation (Welcome / Okay / Say Again / Moment) → amber bg, dark text
//   Order Flow   (Continue / Confirm / Read Back / Complete) → blue bg, white text
//   Escalation   (Request Staff) → red bg, white text
const CONVERSATION: IntentButtonColors = {
  backgroundColor: Colors.acknowledge, // amber (#FFC107)
  color: Colors.primaryButtonTextColor, // near-black
};
const ORDER_FLOW: IntentButtonColors = {
  backgroundColor: Colors.askRepeat, // blue (#006BA6)
  color: Colors.white,
};
// HITL 7.0 retunes ONLY this group to the header's own colour (the theme's
// primary.main, which the AppBar renders). Under 7.0 the header and the
// full-width status band are both permanent horizontal bands, so a third,
// slightly different blue on the most-used pills read as a separate design
// language — the same complaint HITL-1661 fixed for the status band, and fixed
// the same way: reuse a colour already in front of the agent.
//
// Only Order Flow moves. Conversation stays amber and Escalation stays red:
// those two carry meaning (routine vs high-consequence) rather than chrome, and
// HITL-1661 aligned the status band TO them, so changing them now would just
// move the mismatch somewhere else.
const ORDER_FLOW_HITL7: IntentButtonColors = {
  backgroundColor: Colors.daintree, // dark navy (#02263D) — the header
  color: Colors.white,
};
const ESCALATION: IntentButtonColors = {
  backgroundColor: Colors.requestStaff, // red (#FF0048)
  color: Colors.white,
};

// Mirrors the section grouping in MainPageLayout/IntentsSectionGrouped; keep them
// aligned if a section's membership changes.
const HITL3_SECTION_SCHEME: Record<Intents, IntentButtonColors> = {
  [Intents.greet]: CONVERSATION,
  [Intents.acknowledge]: CONVERSATION,
  [Intents.askRepeat]: CONVERSATION,
  [Intents.askMoment]: CONVERSATION,
  [Intents.continue]: ORDER_FLOW,
  [Intents.confirm]: ORDER_FLOW,
  [Intents.readback]: ORDER_FLOW,
  [Intents.complete]: ORDER_FLOW,
  [Intents.requestStaff]: ESCALATION,
};

/**
 * Background + text color for an intent button — the single place both intent
 * layouts get their colors from.
 *
 * - Legacy layout (isHitl3 = false): the intent card's OWN colors, i.e. exactly
 *   as before. The flag-off UI must look unchanged.
 * - HITL 3.0 layout (isHitl3 = true): the fixed section scheme above (Welcome /
 *   Okay / Say Again / Moment amber, Continue / Confirm / Read Back / Complete
 *   blue, Request Staff red) so the grouped bar reads as solid color-coded
 *   groups.
 * - HITL 7.0 (isHitl7 = true, which implies isHitl3): as above, except Order
 *   Flow takes the header's navy so the top and bottom bands match.
 *
 * Either way a non-clickable button (cooldown after a press, or a disabled
 * intent) falls back to the shared disabled grey.
 */
export const getIntentButtonColors = (
  intentCard: Pick<IntentCard, 'intent' | 'color' | 'textColor'>,
  clickable: boolean,
  isHitl3: boolean,
  isHitl7 = false
): IntentButtonColors => {
  const base = isHitl3 ? HITL3_SECTION_SCHEME[intentCard.intent] : undefined;
  // Opt-in and defaulted off, so the legacy caller cannot pick this up by
  // omission — it passes three arguments and keeps the card's own colors.
  const scheme = isHitl7 && base === ORDER_FLOW ? ORDER_FLOW_HITL7 : base;
  return {
    backgroundColor: clickable
      ? scheme?.backgroundColor ?? intentCard.color
      : Colors.alto2,
    color:
      scheme?.color ?? intentCard.textColor ?? Colors.primaryButtonTextColor,
  };
};
